import React, {
  memo,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import getConfig from 'next/config';
import { appAuthContext } from 'context/AuthContext';
import useWindowWidth from 'utils/useWindowWidth';
import useOutsideClickHandler from 'utils/useOutsideClickHandler';
import ProfileDropdown from 'components/Header/components/ProfileDropdown';
import MobileNavigation from 'components/Header/components/MobileNavigation';
import MobileNavItems from 'components/Header/components/MobileNavItems';
import useClickStreamEvent from 'utils/clickstream/useClickStreamEvent';
import useFormattedName from 'utils/useFormattedName';
import { configuratorContext } from 'context/ConfiguratorContext';
import Search from '../Search';
import Chat from '../Chat';
import AiToolkitLink from '../AiToolkitLink/AiToolkitLink';
import styles from './HeaderB2T.module.css';

type HeaderB2TProps = {
  showSearch: boolean;
  setShowSearch: (show: SetStateAction<boolean>) => void;
  setShowAskModal: (show: SetStateAction<boolean>) => void;
  setShowRegModal: (show: SetStateAction<boolean>) => void;
  unanswered: number | null;
  setShowLogoutModal: (show: SetStateAction<boolean>) => void;
};

const HeaderB2T = memo(function HeaderB2Cv2({
  showSearch,
  setShowSearch,
  setShowAskModal,
  setShowRegModal,
  unanswered,
  setShowLogoutModal,
}: HeaderB2TProps) {
  const { user } = useContext(appAuthContext);
  const { azFromConfigurator } = useContext(configuratorContext);

  const { sendEvent } = useClickStreamEvent();
  const windowWidth = useWindowWidth();
  const formattedName = useFormattedName();

  const desktopDropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const showUserCabinet = windowWidth < 512;

  const userGrade =
    user?.data && user?.data.type === 'student' && user?.data?.attributes.level;
  const userGradeName = user?.included && user?.included[0]?.attributes?.name;

  const showAiToolkit = azFromConfigurator?.otvety_ai_toolkit?.groupName === 'target';

  const {
    publicRuntimeConfig: { AI_TOOLKIT_URL },
  } = getConfig();

  const showDropdownHandler = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    sendEvent({
      eventType: 'display',
      eventInfo: 'otvety_header_b2t',
    });
  }, []);

  const sendClickEvent = async (eventInfo: string) => {
    await sendEvent({
      eventType: 'click',
      eventInfo,
    });
  };

  const closeDropDown = () => setShowDropdown(false);
  const dropdownSearchHandler = () => {
    closeDropDown();
    setShowSearch(true);
  };

  useOutsideClickHandler(desktopDropdownRef, () => setShowDropdown(false));

  return (
    <>
      {showSearch && windowWidth > 1023 && <Search setShowSearch={setShowSearch} />}
      <div className={styles.wrapper}>
        <div className={styles.left_side}>
          <a
            className={styles.logo}
            href="https://uchi.ru/profile/students"
            target="_blank"
            rel="noopener"
          />
          {!showSearch && (
            <div className={styles.nav}>
              <div className={styles.dropdown_wrapper} ref={desktopDropdownRef}>
                <div
                  onClick={showDropdownHandler}
                  className={`${styles.nav_dropdown} ${styles.nav__item}`}
                >
                  Вопросы и ответы
                  <div className={styles.chevron} />
                </div>
                {showDropdown && (
                  <ProfileDropdown
                    show={showDropdown}
                    showLogoutModal={() => setShowLogoutModal(true)}
                    setShowDropdown={setShowDropdown}
                    className={styles.dropdown}
                  >
                    <div
                      onClick={() => setShowAskModal(true)}
                      className={`${styles.dropdown__item} ${styles.dropdown__item_active}`}
                    >
                      Задать вопрос
                    </div>
                    <Link href="/unanswered">
                      <a
                        onClick={closeDropDown}
                        className={styles.dropdown__item}
                        data-qa-marker="need_answer_btn"
                      >
                        Нужен ответ
                        {unanswered ? (
                          <span className={`${styles.counter} ${styles.bounce_in}`}>
                            {unanswered}
                          </span>
                        ) : null}
                      </a>
                    </Link>
                    <Link href="/profile">
                      <a onClick={closeDropDown} className={styles.dropdown__item}>
                        Мои вопросы
                      </a>
                    </Link>
                    <Link href="/profile/answers">
                      <a onClick={closeDropDown} className={styles.dropdown__item}>
                        Мои ответы
                      </a>
                    </Link>
                    <div
                      onClick={dropdownSearchHandler}
                      className={`${styles.search} ${styles.dropdown__item}`}
                    >
                      <Image
                        src="https://static.uchi.ru/files/otvety/search.svg"
                        width={20}
                        height={20}
                        alt="search-icon"
                      />
                      <div className={styles.search__text}>Поиск</div>
                    </div>
                  </ProfileDropdown>
                )}
              </div>
              {showAiToolkit && (
                <AiToolkitLink
                  className={styles.nav__item}
                  link={AI_TOOLKIT_URL}
                  event_name="otvety-header-ai-toolkit-click"
                />
              )}
              <div className={styles.nav__item}>
                <a
                  href="https://uchi.ru/marathons/students/progresses"
                  onClick={() => sendClickEvent('otvety_header_marathon_click')}
                  target="_blank"
                  rel="noopener"
                  className={styles.marathon_link}
                >
                  Марафон
                  {userGrade && userGradeName && (
                    <>
                      &nbsp;«
                      <div className={styles.nav__item_grade}>
                        {userGrade}&nbsp;
                        {userGradeName.length > 1
                          ? `${userGradeName}`
                          : `${userGradeName} класс`}
                      </div>
                      »
                    </>
                  )}
                </a>
              </div>
              <a
                target="_blank"
                className={`${styles.full_acces_btn} ${styles.full_acces_btn_mobile}`}
                href="https://uchi.ru/get_premium"
                rel="noopener"
                onClick={() => sendClickEvent('otvety_header_full_access_btn')}
              >
                Полный доступ Учи.ру
              </a>
            </div>
          )}
        </div>

        <div className={styles.right_side}>
          <div className={styles.right_side__btns}>
            {!showSearch && (
              <>
                <div className={styles.user}>
                  <div className={styles.chat_wrapper}>
                    <Chat />
                  </div>
                  <a
                    href="https://uchi.ru/profile/students"
                    target="_blank"
                    rel="noopener"
                    className={styles.user_info}
                  >
                    <img
                      className={styles.user__avatar}
                      src={user?.avatar}
                      alt="avatar"
                    />
                    <div className={styles.user__name}>{formattedName}</div>
                  </a>
                </div>
                <a
                  target="_blank"
                  className={`${styles.full_acces_btn} ${styles.full_acces_btn_desktop}`}
                  href="https://uchi.ru/get_premium"
                  rel="noopener"
                  onClick={() => sendClickEvent('otvety_header_full_access_btn')}
                >
                  Полный доступ Учи.ру
                </a>
              </>
            )}
            {windowWidth < 1024 && (
              <MobileNavigation
                setShowAskModal={setShowAskModal}
                setShowRegModal={setShowRegModal}
                unanswered={unanswered}
                showSearch={showSearch}
                setShowSearch={setShowSearch}
                showCabinet={showUserCabinet}
                showDropdownOpenBtn={false}
                showChat={true}
                profileLinkFromCabinet={true}
              >
                <MobileNavItems
                  unanswered={unanswered}
                  showSearch={showSearch}
                  setShowAskModal={setShowAskModal}
                  setShowSearch={setShowSearch}
                />
              </MobileNavigation>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default HeaderB2T;
